import React, { createContext, useContext, useCallback, useRef } from 'react';

/**
 * NavigationGuardContext
 *
 * Allows child pages to register dirty-check + save/discard callbacks so the
 * parent layout can intercept sidebar navigation when unsaved changes exist.
 *
 * Child pages call:
 *   registerGuard(key, { isDirty: () => bool, onSave: async () => void, onDiscard: () => void })
 *   unregisterGuard(key)
 *
 * The layout calls:
 *   getActiveGuard() → returns the first guard whose isDirty() returns true, or null
 */
const NavigationGuardContext = createContext(null);

export const NavigationGuardProvider = ({ children }) => {
  const guardsRef = useRef({});

  const registerGuard = useCallback((key, guard) => {
    guardsRef.current[key] = guard;
  }, []);

  const unregisterGuard = useCallback((key) => {
    delete guardsRef.current[key];
  }, []);

  /**
   * Returns the first registered guard that reports dirty state, or null.
   * The returned object has { isDirty, onSave, onDiscard }.
   */
  const getActiveGuard = useCallback(() => {
    for (const guard of Object.values(guardsRef.current)) {
      try {
        if (guard.isDirty && guard.isDirty()) {
          return guard;
        }
      } catch {
        // Guard may reference unmounted state — skip it
      }
    }
    return null;
  }, []);

  return (
    <NavigationGuardContext.Provider value={{ registerGuard, unregisterGuard, getActiveGuard }}>
      {children}
    </NavigationGuardContext.Provider>
  );
};

export const useNavigationGuard = () => {
  const ctx = useContext(NavigationGuardContext);
  if (!ctx) {
    throw new Error('useNavigationGuard must be used within a NavigationGuardProvider');
  }
  return ctx;
};

/**
 * Same as useNavigationGuard but returns null when outside a NavigationGuardProvider.
 * Useful for components that may be rendered both inside and outside a guarded layout.
 */
export const useNavigationGuardOptional = () => {
  return useContext(NavigationGuardContext);
};

export default NavigationGuardContext;
